import { logError } from "components/providers/logError";
import dayjs from "dayjs";
import { useState } from "react";

const MAXIMUM_ITEMS = 9;

export type Timestamped<T extends object> = T & {
    lastViewed: string;
};

export const useRecentStack = <T extends object>(key: string) => {
    const [items, setItems] = useState(getItems<T>(key));

    return [
        items.filter(isNotExpired),
        (items: Timestamped<T>[]) => {
            localStorage.setItem(key, JSON.stringify(items));
            setItems(items as Timestamped<T>[]);
        }
    ] as const;
};

export const storeItem = <T extends object>(key: string, item: T, keyFn: (item: T) => string | null) => {
    const storedItem: Timestamped<T> = { ...item, lastViewed: dayjs().toISOString() };

    const items = getItems<Timestamped<T>>(key);
    const existing = items.findIndex((el) => keyFn(el) === keyFn(storedItem));
    if (existing != -1) {
        items.splice(existing, 1);
    }

    items.unshift(storedItem);
    items.splice(MAXIMUM_ITEMS);

    localStorage.setItem(key, JSON.stringify(items));
};

const isNotExpired = <T extends object>(item: Timestamped<T>) =>
    dayjs().diff(dayjs(item.lastViewed), "month", true) < 6;

const getItems = <T extends object>(key: string): Timestamped<T>[] => {
    try {
        return JSON.parse(localStorage.getItem(key) || "[]") as Timestamped<T>[];
    } catch (error) {
        logError(error);
        return [];
    }
};
